import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { useEffect, useState } from "react";
import { Localization } from "@viamap/viamap2-common";
import jsPDF from "jspdf";
import { ViewButtonDownload, ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { MatrikelRapport } from "./MatrikelRapport";
import { MarkRapport } from "./MarkRapport";
import { Feature } from "src/states/ApplicationState";
import { ServitutRapport } from "./ServitutRapport";
import { ServitutRedegoerelseRapport } from "./ServitutRedegoerelseRapport";
import { ManagedWindow } from "src/WindowManager/WindowState";
import { StatTracker } from "src/common/managers/StatTracker";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { Spinner } from "react-bootstrap";
import { Document, Page } from "react-pdf";
import { message } from "./Message";
import { aktIdFormat, makeAsTableData, tableToExcel } from "./DatarapportShared";

export function Datarapport(props: { onGenerate: (data: any, pdf: jsPDF) => void, selected, onSelect, onComplete?: () => void }) {

   switch (props.selected) {
      case "Matrikeloversigt":
         return <MatrikelRapport onGenerate={props.onGenerate} onSelectRapport={props.onSelect} availableRapports={possibleRapports} currentRapport={props.selected} />;
      case "Markoversigt":
         return <MarkRapport onGenerate={props.onGenerate} onSelectRapport={props.onSelect} availableRapports={possibleRapports} currentRapport={props.selected} />;
      case "Servitutoversigt":
         return <ServitutRapport onGenerate={props.onGenerate} onSelectRapport={props.onSelect} availableRapports={possibleRapports} currentRapport={props.selected} />;
      case "Servitutredegørelse":
         return <ServitutRedegoerelseRapport onSelectRapport={props.onSelect} availableRapports={possibleRapports} currentRapport={props.selected} onComplete={props.onComplete} />;
      default:
         return null;
   }

}

const possibleRapports = [
   {
      label: "Matrikeloversigt",
      value: "Matrikeloversigt",
      access: Feature.Matrikeloversigt
   },
   {
      label: "Markoversigt",
      value: "Markoversigt",
      access: Feature.Markoversigt
   },
   {
      label: "Servitutoversigt",
      value: "Servitutoversigt",
      access: Feature.Servitutoversigt
   },
   {
      label: "Servitutredegørelse",
      value: "Servitutredegørelse",
      access: Feature.ServitutRedegoerelse
   }
] as { label: string, value: string, access: string }[];


export function DataRapportDialog(props: { managedWindow?: ManagedWindow, onClose: () => void }) {
   const [pdfData, setPdfData] = useState<jsPDF | null>(null);
   const [data, setData] = useState<unknown[] | null>(null);
   const [dataUrl, setDataUrl] = useState<ArrayBuffer | null>(null);

   const [selectedRapport, setSelectedRapport] = useState<string>("Matrikeloversigt");

   useEffect(() => {
      if (pdfData === null) return;
      const abortController = new AbortController();

      if (abortController.signal.aborted) return;
      setDataUrl(pdfData.output("arraybuffer"));
      return () => {
         abortController.abort();
         setDataUrl(null);
      }

   }, [pdfData, selectedRapport])

   function excelSave(name: string, data: unknown[] | null) {
      switch (selectedRapport) {
         case "Markoversigt": {
            let tableData = makeAsTableData(data as any[], (item: object, sfe) => {
               if (!("properties" in item)) {
                  return undefined;
               }
               let markD = item.properties as any;
               return {
                  "Adresse": sfe?.properties["adresse"],
                  "SFEnummer": sfe?.properties["SFEnummer"],
                  "Markblok": markD["Markblok"],
                  "Marknummer": markD["Marknr"],
                  "CVR": markD["CVR"],
                  "Afgrødkode": markD["Afgkode"],
                  "Afgrød": markD["Afgroede"],
                  "Areal": parseFloat(markD["IMK_areal"]),
                  "Anmeldtareal": parseFloat(markD["GBanmeldt"]),
                  "Overlap med ejendom": parseFloat(markD["IMK_areal"]) * markD["overlap"] / markD["area"],
               }
            }, "marker");
            tableToExcel(tableData, "Markoversigt.xlsx");
            break;
         }
         case "Servitutoversigt": {
            let tableData = makeAsTableData(data as any[], (item: object, bfe) => {
               let servD = item as any
               return {
                  "Adresse": bfe["adresse"],
                  "BFEnummer": bfe["BFEnummer"],
                  "Prioritet": servD["prioritetNummer"],
                  "Akt Id": aktIdFormat(servD["aktId"] || servD["aktAliasId"]),
                  "Tekst": servD["tekstSummarisk"] || servD["tekster"].map((t: any) => t.tekst ?? t).join(", "),
                  "Dato": servD["tinglysningsDato"],
               }
            }, "servitutter");
            tableToExcel(tableData, "Servitutoversigt.xlsx");
            break;
         }
         case "Servitutredegørelse": {
            const payload = data as any;
            const rows = payload?.matrix?.rows || [];
            const columns = payload?.matrix?.columns || [];
            const tableData = rows.map((row: any) => {
               const base: Record<string, string> = {
                  "Akt Id": row.displayAktId,
                  "Prioritet": row.priority,
                  "Tekst": row.summary,
                  "PDF status": row.pdfStatus,
                  "PDF kilde": row.pdfSource || "",
               };
               columns.forEach((bfe: number) => {
                  base[`BFE ${bfe}`] = row.includedBfes?.includes(bfe) ? "x" : "";
               });
               return base;
            });
            tableToExcel(tableData, "Servitutredegørelse.xlsx");
            break;
         }
         case "Matrikeloversigt": {
            let tableData = makeAsTableData(data as any[], (item: object, sfe) => {
               return {
                  "SFEnummer": sfe["BFEnummer"],
                  "Adresse": sfe["adresse"],
                  "Matrikelnummer": item["matrikelnummer"],
                  "Ejerlav": (item as any)?.jordstykkeLiggerIEjerlav?.ejerlavsnavn,
                  "Areal": item["registreretAreal"],
                  "vejareal": item["vejareal"],
               }
            }, "jordstykkeSamlesISamletFastEjendom.nodes");
            tableToExcel(tableData, "Matrikeloversigt.xlsx");
            break;
         }
         default:
            message.error("Unknown report type for Excel download, Please contact support@viamap.net");
            console.log("Unknown report type for Excel download:", selectedRapport);
            console.log("data", data);
      }
   }

   if (pdfData !== null) {
      return (
         <AdvancedDragModal
            window={props.managedWindow}
            PosDefault={{ top: "100px", left: "100px", width: "500px", height: "700px" }}
            title={Localization.getText("Preview")} onClose={() => { props.onClose() }}
            subHeaderContent={<>
               <ViewButtonIcon title={Localization.getText("Go back w keyword")} onClick={() => { setPdfData(null); }}><MIBIcon icon="GoBack" /></ViewButtonIcon>
               <ViewButtonDownload title={Localization.getText("Print")} options={[
                  {
                     type: "pdf", text: "Hent som PDF", onClick: async () => {
                        docSave(selectedRapport, pdfData);
                        return true;
                     }
                  },
                  {
                     type: "xlsx", text: "Hent som Excel", onClick: async () => {
                        console.log("Data to be downloaded as Excel:", data);
                        excelSave(selectedRapport, data);
                        return true;
                     }
                  }
               ]} />
               {/* <ViewButtonIcon title={Localization.getText("Save")} onClick={() => docSave(selectedRapport, data)}><MIBIcon icon="download" /></ViewButtonIcon> */}

            </>
            }
         >
            <ADModalBody>
               {dataUrl ?
                  <Document file={dataUrl} className={"DatarapportPDFViewer"} >
                     {Array.from({ length: pdfData.getNumberOfPages() ?? 0 }, (_, i) => i).map((pageIndex) => (
                        <Page pageIndex={pageIndex} key={pageIndex} />
                     ))}
                  </Document>
                  : <Spinner />}
            </ADModalBody>
         </AdvancedDragModal>
      )
   }

   return (
      <AdvancedDragModal
         window={props.managedWindow}
         PosDefault={{ top: "100px", left: "100px", width: "382px" }}
         title={Localization.getText("Data report")} onClose={props.onClose}>
         <Datarapport onGenerate={(data: any, pdf: jsPDF) => {
            ActionTracker.track("DataReport", selectedRapport || "Ukendt", "GenerateReport");
            StatTracker.count("DataReport", selectedRapport || "Ukendt");
            setData(data);
            setPdfData(pdf);
         }}
            selected={selectedRapport}
            onSelect={setSelectedRapport}
            onComplete={props.onClose}
         />
      </AdvancedDragModal>
   )
}

function docSave(name: string, doc?: jsPDF | null) {
   if (doc) {
      doc.save(name + ".pdf");
   } else {
      console.error("No document to save");
   }
}